import { createAction } from '@reduxjs/toolkit';
import { ISubmitUserRatingFeedbackRequest } from '@sparx/grpc/lib/generated/apis/sparx/interaction/feedback/v1/feedback';
import { Rating } from '@sparx/grpc/lib/generated/apis/sparx/interaction/feedback/v1/feedback_pb';
import { ITrainingProgress } from '@sparx/grpc/lib/generated/apis/sparx/training/progress/v1/trainingprogress.d';
import { CompletePayload, TrainingModuleName, UpdatePayload } from './types';

/**
 * Redux actions for the teacher training module in Sparx Web which shows a teacher how to use the
 * student experience.
 *
 * Inspired by tpclient2 'reducers/training/index.ts'
 */

const START_TRAINING = 'START_TRAINING';
const SET_TRAINING_MODULE = 'SET_TRAINING_MODULE';
const CALCULATE_MODULE_COMPLETION = 'CALCULATE_MODULE_COMPLETION';
const COMPLETE_TRAINING_TASK = 'COMPLETE_TRAINING_TASK';
const SET_SELECTED_STEP = 'SET_SELECTED_STEP';
const SET_NEED_HELP_VIDEO = 'SET_NEED_HELP_VIDEO';
const SET_WATCHING_HELP_VIDEO = 'SET_WATCHING_HELP_VIDEO';
const UPDATE_STORE = 'UPDATE_STORE';
const RESET_TRAINING_PROGRESS = 'RESET_TRAINING_PROGRESS';
const RESYNC_TRAINING_PROGRESS = 'RESYNC_TRAINING_PROGRESS';
const SUBMIT_USER_RATING_FEEDBACK = 'SUBMIT_USER_FEEDBACK';
const SHOW_FEEDBACK_DIALOG = 'SHOW_FEEDBACK_DIALOG';

/**
 * Initialise the training system by fetching the user's training progress from the server and
 * loading it into Redux, and then starting a training module
 */
export const startTraining = createAction<TrainingModuleName>(START_TRAINING);

/**
 * Set training module starts a new training module, fetching the user's current progress
 * @param module
 */
export const setTrainingModule = createAction<TrainingModuleName>(SET_TRAINING_MODULE);

/**
 * Complete the step of the training with the given stepID and moduleName
 * @param stepID and moduleName
 */
export const completeTrainingTask = createAction<CompletePayload>(COMPLETE_TRAINING_TASK);

/**
 * Calculate if the training module is complete
 * @param module
 */
export const calculateModuleCompletion = createAction<TrainingModuleName>(
    CALCULATE_MODULE_COMPLETION,
);

/**
 * Set the step at the given index as the one currently selected
 * @param numberindex of the selected step
 */
export const setSelectedStep = createAction<number>(SET_SELECTED_STEP);

/**
 * Activate the need help video
 */
export const setNeedHelpVideo = createAction<void>(SET_NEED_HELP_VIDEO);

/**
 * Set whether the user is watching the help video
 * @param boolean
 */
export const setWatchingHelpVideo = createAction<boolean>(SET_WATCHING_HELP_VIDEO);

/**
 * Update the value of a training module in the store
 * @param module The updated value of the module
 * @param force Whether to handle module_data that would be set dynamically from a user's action,
 * e.g. completed_time
 */
export const updateStoredTrainingProgress = createAction<UpdatePayload>(UPDATE_STORE);

/**
 * Remove all module data for the current user
 */
export const resetTrainingProgress = createAction<void>(RESET_TRAINING_PROGRESS);

/**
 * Submit user feedback.
 */
export const submitUserRatingFeedback = createAction<ISubmitUserRatingFeedbackRequest>(
    SUBMIT_USER_RATING_FEEDBACK,
);

/**
 * Resync the training progress in redux state
 */
export const resyncTrainingProgress = createAction<ITrainingProgress | null>(
    RESYNC_TRAINING_PROGRESS,
);

/**
 * Show the feedback dialog
 */
export const showFeedbackDialog = createAction<Rating.AsObject>(SHOW_FEEDBACK_DIALOG);
